import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  Building2,
  CalendarClock,
  CheckCircle2,
  Clock,
  FileCheck2,
  Info,
  ShieldAlert,
  TriangleAlert,
  XCircle,
  Zap,
} from 'lucide-react';
import { useI18n, useTranslation } from '@solara/i18n';
import {
  BarChart,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  EmptyState,
  Sparkline,
  cn,
} from '@solara/ui-shared';
import {
  useAdminInvoices,
  useApprovals,
  useTenants,
  useWebhooks,
  useCertificates,
} from '@solara/api-client/react';
import { MOCK_HEALTH, MOCK_TICKSTAR_SLA } from '../data/mockData';

export default function DashboardPage() {
  const { t } = useTranslation('admin');
  const { t: tTs } = useTranslation('tickstar');
  const { locale } = useI18n();
  const aedFmt = new Intl.NumberFormat(locale, { style: 'currency', currency: 'AED' });
  const numberFmt = new Intl.NumberFormat(locale);
  const pctFmt = new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 2 });

  const tenantsQuery = useTenants();
  const adminInvoicesQuery = useAdminInvoices({ limit: 200 });
  const pendingApprovalsQuery = useApprovals({ state: 'PENDING' });
  // Webhooks tile is rendered through the admin console TickStar page; keep
  // a small fetch here for the alert count.
  const webhooksQuery = useWebhooks({ limit: 10 });
  void webhooksQuery;
  const certsQuery = useCertificates();

  // FTA channel watch: derive a live alert if the EmaraTax mTLS cert is within
  // 30 days of expiry (or expired). A lapse takes down all TDD reporting.
  const emaraMtlsAlert = useMemo(() => {
    const c = (certsQuery.data ?? []).find(
      (x) => x.type === 'mtls-emaratax' || x.type === 'mtls_emaratax',
    );
    if (!c) return null;
    const days = Math.floor((new Date(c.notAfter).getTime() - Date.now()) / 86400_000);
    if (days >= 30) return null;
    return {
      id: 'emaratax-mtls-expiry',
      severity: (days < 0 ? 'critical' : days < 14 ? 'critical' : 'warning') as 'critical' | 'warning',
      title:
        days < 0
          ? `EmaraTax mTLS certificate expired ${-days} d ago — FTA channel down`
          : `EmaraTax mTLS certificate expires in ${days} d`,
      firedAt: new Date().toISOString(),
    };
  }, [certsQuery.data]);

  const tenants = tenantsQuery.data ?? [];
  const adminInvoices = adminInvoicesQuery.data ?? [];

  const tenantStats = useMemo(() => {
    const counts = { ACTIVE: 0, PENDING: 0, SUSPENDED: 0, TERMINATED: 0 } as Record<string, number>;
    for (const tn of tenants) counts[tn.status] = (counts[tn.status] ?? 0) + 1;
    return counts;
  }, [tenants]);

  const invoiceStats = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    let last24 = 0;
    let totalVat = 0;
    const breakdown: Record<'AP' | 'AB' | 'RE' | 'PENDING', number> = {
      AP: 0,
      AB: 0,
      RE: 0,
      PENDING: 0,
    };
    for (const inv of adminInvoices) {
      if (inv.issueDate.slice(0, 10) === today) last24++;
      totalVat += Number(inv.vatMinor) / 100;
      const bucket =
        inv.mlsState === 'AP' || inv.status === 'ACKNOWLEDGED' || inv.status === 'ARCHIVED'
          ? 'AP'
          : inv.mlsState === 'AB' ||
            inv.status === 'DELIVERED' ||
            inv.status === 'SENDING' ||
            inv.status === 'SIGNED'
          ? 'AB'
          : inv.mlsState === 'RE' || inv.status === 'REJECTED' || inv.status === 'FAILED'
          ? 'RE'
          : 'PENDING';
      breakdown[bucket]++;
    }
    return { last24, totalVat, breakdown };
  }, [adminInvoices]);

  // Upcoming TDD reuses MOCK_HEALTH for sparklines; TDD-by-tenant isn't
  // exposed from backend yet.
  const upcomingTdd: Array<never> = [];

  const peakThroughput = Math.max(...MOCK_HEALTH.throughputSeries);
  const currentRejection =
    MOCK_HEALTH.rejectionRateSeries[MOCK_HEALTH.rejectionRateSeries.length - 1] ?? 0;

  const topTenants = useMemo(() => {
    const map = new Map<string, { name: string; total: number; count: number }>();
    for (const inv of adminInvoices) {
      const cur = map.get(inv.tenantId) ?? {
        name: inv.tendant?.legalNameEn ?? inv.sellerName ?? inv.tenantId,
        total: 0,
        count: 0,
      };
      cur.total += Number(inv.totalMinor) / 100;
      cur.count++;
      map.set(inv.tenantId, cur);
    }
    return Array.from(map.values())
      .sort((a, b) => b.total - a.total)
      .slice(0, 5);
  }, [adminInvoices]);

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">{t('dashboard.title')}</h1>
        <p className="text-fg-muted">{t('dashboard.subtitle')}</p>
      </header>

      <section className="grid gap-4 grid-cols-1 sm:grid-cols-2 xl:grid-cols-4">
        <Stat
          icon={<Building2 className="h-5 w-5" />}
          label={t('dashboard.kpi.tenants')}
          value={numberFmt.format(tenants.length)}
          hint={t('dashboard.kpi.tenantsHint', {
            active: tenantStats.ACTIVE ?? 0,
            pending: (pendingApprovalsQuery.data ?? []).length,
          })}
        />
        <Stat
          icon={<FileCheck2 className="h-5 w-5" />}
          label={t('dashboard.kpi.invoices24h')}
          value={numberFmt.format(invoiceStats.last24 || MOCK_HEALTH.throughputSeries.reduce((s, n) => s + n, 0))}
          hint={`${adminInvoices.length} total`}
        />
        <Stat
          icon={<Zap className="h-5 w-5" />}
          label={t('dashboard.kpi.throughput')}
          value={`${numberFmt.format(peakThroughput)}/h`}
          hint={t('dashboard.kpi.throughputHint')}
          tone={peakThroughput > 240 ? 'success' : 'default'}
        />
        <Stat
          icon={<FileCheck2 className="h-5 w-5" />}
          label={t('dashboard.kpi.vat')}
          value={aedFmt.format(invoiceStats.totalVat)}
        />
      </section>

      {/* Invoice status breakdown — spec § Modul 7 */}
      <section>
        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle>{t('dashboard.statusBreakdown')}</CardTitle>
            <p className="text-xs text-fg-subtle mt-0.5">{t('dashboard.statusBreakdownHint')}</p>
          </CardHeader>
          <CardBody className="grid gap-3 sm:grid-cols-4">
            <StatusCard
              tone="success"
              icon={<CheckCircle2 className="h-5 w-5" />}
              label={t('dashboard.status.AP')}
              value={numberFmt.format(invoiceStats.breakdown.AP)}
            />
            <StatusCard
              tone="info"
              icon={<Clock className="h-5 w-5" />}
              label={t('dashboard.status.AB')}
              value={numberFmt.format(invoiceStats.breakdown.AB)}
            />
            <StatusCard
              tone="danger"
              icon={<XCircle className="h-5 w-5" />}
              label={t('dashboard.status.RE')}
              value={numberFmt.format(invoiceStats.breakdown.RE)}
            />
            <StatusCard
              tone="warning"
              icon={<Clock className="h-5 w-5" />}
              label={t('dashboard.status.PENDING')}
              value={numberFmt.format(invoiceStats.breakdown.PENDING)}
            />
          </CardBody>
        </Card>
      </section>

      {/* TickStar partner SLA — accent partnership tile */}
      <section>
        <Card className="rounded-2xl border-amber-100">
          <CardHeader>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <CardTitle className="flex items-center gap-2">
                  {tTs('admin.tabs.sla')}
                </CardTitle>
                <p className="text-xs text-fg-subtle mt-0.5">{tTs('admin.page.subtitle')}</p>
              </div>
              <Button asChild variant="outline" size="sm">
                <Link to="/tickstar">{t('nav.tickstar')}</Link>
              </Button>
            </div>
          </CardHeader>
          <CardBody className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {MOCK_TICKSTAR_SLA.map((s) => {
              const breach = s.kind === 'latency' && s.measuredNumeric > s.targetNumeric;
              return (
                <div key={s.bucket} className="rounded-xl border border-border p-3 space-y-1">
                  <div className="text-xs uppercase tracking-wide text-fg-subtle">
                    {tTs(`admin.sla.bucket.${s.bucket}`)}
                  </div>
                  <div className={cn('text-lg font-semibold tabular-nums', breach ? 'text-danger' : 'text-fg')}>
                    {s.measured}
                  </div>
                  <div className="text-[10px] text-fg-subtle">
                    {tTs('admin.sla.header.target')}: {s.target}
                  </div>
                </div>
              );
            })}
          </CardBody>
        </Card>
      </section>

      <section className="grid gap-6 grid-cols-1 lg:grid-cols-3">
        <Card className="lg:col-span-2 rounded-2xl">
          <CardHeader>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <CardTitle>{t('dashboard.throughputTitle')}</CardTitle>
                <p className="text-xs text-fg-subtle mt-0.5">{t('dashboard.throughputSubtitle')}</p>
              </div>
              <Badge tone="success">peak {numberFmt.format(peakThroughput)}/h</Badge>
            </div>
          </CardHeader>
          <CardBody>
            <Sparkline
              values={MOCK_HEALTH.throughputSeries}
              width={640}
              height={80}
              stroke="rgb(var(--color-brand-500))"
              fill="rgb(var(--color-brand-100) / 0.6)"
              className="w-full max-w-full"
            />
          </CardBody>
        </Card>

        <Card className="rounded-2xl">
          <CardHeader>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <CardTitle>{t('dashboard.rejectionTitle')}</CardTitle>
                <p className="text-xs text-fg-subtle mt-0.5">{t('dashboard.rejectionSubtitle')}</p>
              </div>
              <Badge tone={currentRejection < 0.03 ? 'success' : currentRejection < 0.06 ? 'warning' : 'danger'}>
                {pctFmt.format(currentRejection)}
              </Badge>
            </div>
          </CardHeader>
          <CardBody>
            <Sparkline
              values={MOCK_HEALTH.rejectionRateSeries.map((r) => r * 1000)}
              width={300}
              height={70}
              stroke="rgb(var(--color-warning))"
              fill="rgb(var(--color-warning) / 0.15)"
              className="w-full max-w-full"
            />
          </CardBody>
        </Card>
      </section>

      <section className="grid gap-6 grid-cols-1 lg:grid-cols-3">
        <Card className="lg:col-span-2 rounded-2xl">
          <CardHeader>
            <CardTitle>{t('dashboard.moduleHealthTitle')}</CardTitle>
          </CardHeader>
          <CardBody>
            <BarChart
              data={MOCK_HEALTH.modulep95.map((m) => ({
                label: m.module,
                value: m.p95Ms,
                fill:
                  m.status === 'healthy'
                    ? 'rgb(var(--color-success))'
                    : m.status === 'degraded'
                    ? 'rgb(var(--color-warning))'
                    : 'rgb(var(--color-danger))',
              }))}
              format={(n) => `${numberFmt.format(n)} ms`}
            />
          </CardBody>
        </Card>

        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle>{t('dashboard.complianceTitle')}</CardTitle>
          </CardHeader>
          <CardBody className="space-y-3 text-sm">
            <Row label={t('dashboard.compliance.iso27001')} value={<Badge tone="success">{t('dashboard.compliance.active')}</Badge>} />
            <Row label={t('dashboard.compliance.iso22301')} value={<Badge tone="success">{t('dashboard.compliance.active')}</Badge>} />
            <Row label={t('dashboard.compliance.fta')} value={<Badge tone="warning">{t('dashboard.compliance.pending')}</Badge>} />
            <Row label={t('dashboard.compliance.peppol')} value={<Badge tone="warning">{t('dashboard.compliance.pending')}</Badge>} />
          </CardBody>
        </Card>
      </section>

      {/* Upcoming TDD — cross-tenant operator view (spec § Modul 6) */}
      <section>
        <Card className="rounded-2xl">
          <CardHeader>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <CardTitle>{t('dashboard.upcomingTddTitle')}</CardTitle>
                <p className="text-xs text-fg-subtle mt-0.5">{t('dashboard.upcomingTddSubtitle')}</p>
              </div>
              <Link to="/tickstar" className="text-xs text-fg-muted underline">
                TickStar DLQ →
              </Link>
            </div>
          </CardHeader>
          <CardBody>
            <EmptyState icon={<CalendarClock className="h-6 w-6" />} title={t('dashboard.upcomingTddEmpty')} />
          </CardBody>
        </Card>
      </section>

      <section className="grid gap-6 grid-cols-1 lg:grid-cols-3">
        <Card className="lg:col-span-2 rounded-2xl">
          <CardHeader>
            <CardTitle>{t('dashboard.topTenants')}</CardTitle>
          </CardHeader>
          <CardBody>
            <BarChart
              data={topTenants.map((tn) => ({ label: tn.name, value: tn.total }))}
              format={(n) => aedFmt.format(n)}
            />
          </CardBody>
        </Card>

        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle>{t('dashboard.alertsTitle')}</CardTitle>
          </CardHeader>
          <CardBody className="space-y-3">
            {emaraMtlsAlert ? (
              <Link
                key={emaraMtlsAlert.id}
                to="/certificates"
                className={cn(
                  'flex items-start gap-2 rounded-xl border p-3 transition-colors hover:bg-surface-muted',
                  emaraMtlsAlert.severity === 'critical'
                    ? 'border-danger/40 bg-danger/5'
                    : 'border-warning/40 bg-warning/5',
                )}
              >
                {emaraMtlsAlert.severity === 'critical' ? (
                  <ShieldAlert className="h-4 w-4 text-danger shrink-0 mt-0.5" />
                ) : (
                  <TriangleAlert className="h-4 w-4 text-warning shrink-0 mt-0.5" />
                )}
                <div className="text-xs">
                  <div className="font-medium text-fg">{emaraMtlsAlert.title}</div>
                  <div className="text-fg-subtle mt-0.5">EmaraTax channel · Certificates →</div>
                </div>
              </Link>
            ) : null}
            {MOCK_HEALTH.activeAlerts.map((a) => (
              <Link
                key={a.id}
                to="/monitoring"
                className={cn(
                  'flex items-start gap-2 rounded-xl border p-3 transition-colors hover:bg-surface-muted',
                  a.severity === 'critical'
                    ? 'border-danger/40 bg-danger/5'
                    : a.severity === 'warning'
                    ? 'border-warning/40 bg-warning/5'
                    : 'border-info/40 bg-info/5',
                )}
              >
                {a.severity === 'critical' ? (
                  <ShieldAlert className="h-4 w-4 text-danger shrink-0 mt-0.5" />
                ) : a.severity === 'warning' ? (
                  <TriangleAlert className="h-4 w-4 text-warning shrink-0 mt-0.5" />
                ) : (
                  <Info className="h-4 w-4 text-info shrink-0 mt-0.5" />
                )}
                <div className="text-xs">
                  <div className="font-medium text-fg">{a.title}</div>
                  <div className="text-fg-subtle mt-0.5">
                    {new Date(a.firedAt).toLocaleString(locale)}
                  </div>
                </div>
              </Link>
            ))}
            {MOCK_HEALTH.activeAlerts.length === 0 && !emaraMtlsAlert ? (
              <div className="text-fg-muted text-sm flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-success" />
                All clear.
              </div>
            ) : null}
          </CardBody>
        </Card>
      </section>
    </div>
  );
}

function Stat({
  icon,
  label,
  value,
  hint,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  hint?: string;
  tone?: 'success' | 'danger' | 'warning' | 'default';
}) {
  return (
    <Card>
      <CardBody className="p-6 flex flex-col gap-2">
        <div className="flex items-center justify-between text-fg-muted">
          <span className="text-xs uppercase tracking-wide">{label}</span>
          <span
            className={
              tone === 'success'
                ? 'text-success'
                : tone === 'danger'
                ? 'text-danger'
                : tone === 'warning'
                ? 'text-warning'
                : 'text-fg-subtle'
            }
          >
            {icon}
          </span>
        </div>
        <span className="text-3xl font-semibold tracking-tight num">{value}</span>
        {hint ? <span className="text-xs text-fg-subtle">{hint}</span> : null}
      </CardBody>
    </Card>
  );
}

function StatusCard({
  tone,
  icon,
  label,
  value,
}: {
  tone: 'success' | 'danger' | 'info' | 'warning';
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  const accent: Record<typeof tone, string> = {
    success: 'text-success bg-emerald-50',
    danger: 'text-danger bg-red-50',
    info: 'text-info bg-cyan-50',
    warning: 'text-warning bg-amber-50',
  };
  return (
    <div className="flex items-center gap-3 rounded-xl border border-border p-3">
      <div className={cn('h-10 w-10 grid place-items-center rounded-xl shrink-0', accent[tone])}>
        {icon}
      </div>
      <div>
        <div className="text-xs uppercase tracking-wide text-fg-subtle">{label}</div>
        <div className="text-xl font-semibold num">{value}</div>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-fg-muted">{label}</span>
      <span>{value}</span>
    </div>
  );
}
